import React, { useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAppContext } from '@/context/AppContext'
import { getPagePath, findPageBySlug } from '@/utils/pagePaths'
import PublicPageRoute from './PublicPageRoute'

/**
 * Redirect component for pages with parents
 * If a page has a parent, uses replaceState to redirect from /{page} to /{parent}/{page}
 * Otherwise, renders PublicPageRoute
 */
function PageRedirect({ match: matchProp }) {
    const params = useParams()
    const location = useLocation()
    const navigate = useNavigate()
    const match = matchProp ?? { params }
    const { content } = useAppContext()
    const urlSlug = match?.params?.slug || params.slug || ''
    const hasRedirected = useRef(false)
    
    const shouldCheckRedirect = urlSlug && !urlSlug.includes('/')
    
    useEffect(() => {
        if (!shouldCheckRedirect || hasRedirected.current) return
        
        const pages = content?.pages || {}
        const result = findPageBySlug(urlSlug, pages)
        const page = result?.page
        
        const parentId = page?.parentId || page?.parentUrl
        if (page && parentId) {
            const fullPath = getPagePath(page, pages)
            const pageSlug = page.slug || page.url
            if (fullPath !== pageSlug) {
                hasRedirected.current = true
                const search = location?.search || ''
                const hash = location?.hash || ''
                const redirectTo = `/${fullPath}${search}${hash}`
                navigate(redirectTo, { replace: true })
                return
            }
        }
    }, [urlSlug, shouldCheckRedirect, content, location, navigate])
    
    if (!shouldCheckRedirect) {
        return <PublicPageRoute match={match} />
    }
    
    const pages = content?.pages || {}
    const result = findPageBySlug(urlSlug, pages)
    const page = result?.page
    
    const parentId = page?.parentId || page?.parentUrl
    if (page && parentId) {
        const fullPath = getPagePath(page, pages)
        const pageSlug = page.slug || page.url
        if (fullPath !== pageSlug) {
            return null
        }
    }
    
    return <PublicPageRoute match={match} />
}

PageRedirect.propTypes = {
    match: PropTypes.object,
}

export default PageRedirect
